/**
 * Durable questionnaire-builder chat turn (BullMQ + Socket.IO).
 *
 * A turn is scoped to a draft. The browser subscribes to
 * `questionnaire-draft:{draftId}` and receives stage/status events that carry
 * `turnId`. Reload resume is: subscribe by draftId, then GET the active turn.
 */

import type { StudyObjectiveChange } from "./revision.generated";
import type { QuestionnaireChatState } from "./builder-wire";
import type {
  QuestionnaireHttpOperation,
  QuestionnaireRefusalParams,
} from "./operations";

/** Asset-only operations a collect card may carry into its durable turn. */
export type QuestionnaireCollectedAssetOperation = Extract<
  QuestionnaireHttpOperation,
  {
    op:
      | "add_asset"
      | "edit_asset"
      | "set_asset_media"
      | "set_declared_asset_types"
      | "collect_assets";
  }
>;

export const QUESTIONNAIRE_CHAT_TURN_STATUSES = [
  "queued",
  "running",
  "completed",
  "failed",
  "cancelled",
] as const;

export type QuestionnaireChatTurnStatus =
  (typeof QUESTIONNAIRE_CHAT_TURN_STATUSES)[number];

/**
 * Server truth only (plan S4.4, L26): the builder's empty canvas names the
 * stage the server reported. `persisting` precedes every commit, not only a
 * build's, so its label is a neutral "Saving…". `total` on the stage event is
 * the one counter; there is no `completed` counter, because nothing produced one.
 */
export const QUESTIONNAIRE_CHAT_TURN_STAGES = [
  "routing",
  "planning",
  "authoring",
  "assembling",
  "persisting",
] as const;

export type QuestionnaireChatTurnStage =
  (typeof QUESTIONNAIRE_CHAT_TURN_STAGES)[number];

export const QUESTIONNAIRE_CHAT_TURN_TERMINAL_STATUSES: ReadonlyArray<QuestionnaireChatTurnStatus> =
  ["completed", "failed", "cancelled"];

export function isQuestionnaireChatTurnTerminal(
  status: QuestionnaireChatTurnStatus,
): boolean {
  return (
    QUESTIONNAIRE_CHAT_TURN_TERMINAL_STATUSES as ReadonlyArray<string>
  ).includes(status);
}

/**
 * A UI fact a turn carries instead of a message (plan 09 S4.2, L24). The turn
 * writes its own user message with the command on it, and the builder renders
 * it with its own copy; the model never reads a sentence the UI wrote.
 *
 * - `collect_receipt`: the assets a collect card just added. It answers an
 *   open collect card's clarification, or supersedes another open one. On an
 *   attach card (`collect.attachTo`) it names the assets of the card's items
 *   that carry a file; empty, the user continued without one, which only an
 *   attach card takes (`collectReceiptFitsCard`). A receipt that carries a
 *   `collect_assets` operation is named by the server: the assets of that
 *   family once the operation is applied.
 * - `translate`: translate the questionnaire into `language`.
 * - `use_new_assets`: bring the assets just added into the questions.
 * - `set_objective`: the objective panel's edit.
 * - `resume`: the Retry of a turn that stopped after its commit landed
 *   (#3): run what that commit left undone. Its words are saved and its
 *   record answered, so the resumed turn writes no message of its own and
 *   reads that turn's words. Accepted only while that turn is the draft's
 *   latest and its commit the draft's revision.
 *
 * Every command other than a collect receipt supersedes an open
 * clarification.
 */
export const QUESTIONNAIRE_TURN_COMMAND_KINDS = [
  "collect_receipt",
  "translate",
  "use_new_assets",
  "set_objective",
  "resume",
] as const;

export type QuestionnaireTurnCommandKind =
  (typeof QUESTIONNAIRE_TURN_COMMAND_KINDS)[number];

export type QuestionnaireTurnCommand =
  | {
      kind: "collect_receipt";
      assetKeys: Array<string>;
      /**
       * Asset mutations accepted with this receipt. The durable turn applies
       * them with the receipt transcript in one revision CAS; the browser
       * must not first submit a separate operations request.
       */
      assetOperations?: Array<QuestionnaireCollectedAssetOperation>;
    }
  | { kind: "translate"; language: string }
  | { kind: "use_new_assets"; assetKeys: Array<string> }
  | {
      kind: "set_objective";
      studyGoals: {
        decision: string;
        /** Omitted keeps the stored bullets; `[]` clears them. */
        learn?: Array<string>;
        source: "confirmed" | "edited";
      };
      /**
       * What the edit changes against the stored objective. The server
       * derives it once, when it enqueues the turn, and stores it on the
       * turn's input: the enqueue answer, the readback, the turn's message
       * and the restructure all carry it, and the builder's bubble shows it.
       * A client never sends it. Absent on the command a client sends, and
       * on turns and messages recorded before 2026-09-25.
       */
      change?: StudyObjectiveChange;
    }
  | { kind: "resume" };

/**
 * The builder's interface languages, as wire codes (the frontend's i18n
 * locales; its `pt-BR` is `pt`). A chat turn carries the user's as a typed
 * fact: every text the user reads in the chat is written in it, whatever
 * language the questionnaire is in, which is the respondents' (QA4).
 */
export const QUESTIONNAIRE_INTERFACE_LANGUAGES = ["en", "es", "fr", "pt"] as const;

export type QuestionnaireInterfaceLanguage =
  (typeof QUESTIONNAIRE_INTERFACE_LANGUAGES)[number];

/** A turn stored before the interface language was kept reads as English. */
export const DEFAULT_QUESTIONNAIRE_INTERFACE_LANGUAGE: QuestionnaireInterfaceLanguage =
  "en";

/** Socket.IO subscribe payload for a draft-scoped turn room. */
export interface SubscribeQuestionnaireDraftPayload {
  draftId: string;
}

/**
 * The builder's Socket.IO names, one owner for the gateway and the browser:
 * the client sends `subscribe`/`unsubscribe` with a
 * `SubscribeQuestionnaireDraftPayload`; the gateway joins the draft's room and
 * emits a `QuestionnaireChatTurnStageEvent` on `turnStage(draftId)` and a
 * `QuestionnaireChatTurnStatusEvent` on `turnStatus(draftId)`.
 */
export const QUESTIONNAIRE_DRAFT_SOCKET = {
  subscribe: "subscribe-questionnaire-draft",
  unsubscribe: "unsubscribe-questionnaire-draft",
  room: (draftId: string) => `questionnaire-draft:${draftId}`,
  turnStage: (draftId: string) => `questionnaire-draft:${draftId}:turn-stage`,
  turnStatus: (draftId: string) => `questionnaire-draft:${draftId}:turn-status`,
} as const;

export interface QuestionnaireChatTurnStageEvent {
  draftId: string;
  turnId: string;
  stage: QuestionnaireChatTurnStage;
  total?: number;
}

export interface QuestionnaireChatTurnStatusEvent {
  draftId: string;
  turnId: string;
  status: QuestionnaireChatTurnStatus;
  stage?: QuestionnaireChatTurnStage | null;
  total?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  failureCodes?: Array<string> | null;
  failureDetails?: Array<QuestionnaireChatTurnFailureDetail> | null;
  /**
   * The failing owner's own retry verdict. Persisted on the turn row, so the
   * live event, a replay on subscribe and a readback all carry it. Null or
   * absent means the owner gave none, and the client offers no retry.
   */
  retryable?: boolean | null;
  draftRevisionAfter?: number | null;
  timestamp: string;
}

/**
 * Bounded, non-content diagnosis retained when a durable generation turn
 * fails. These are structural identifiers only: never an error message,
 * provider response, objective, respondent copy or asset text.
 */
export interface QuestionnaireChatTurnFailureDetail {
  topicKey: string;
  code: string;
  reasonCode: string;
  attempts: number;
  fieldPath?: string;
  errorType?: string;
  /**
   * The refusal's structural facts (`readStructuralRefusalParams`): what the
   * builder fills `reasonCode`'s catalog line from.
   */
  params?: QuestionnaireRefusalParams;
}

/**
 * Cancel is a request (plan S4.4, L25). A queued turn is cancelled at once; a
 * running turn is asked to stop, and its terminal status event says what
 * happened: `cancelled` when nothing committed, `completed` when a commit
 * landed first. False when the turn had already ended.
 */
export interface CancelQuestionnaireChatTurnResponse {
  cancelRequested: boolean;
}

export interface EnqueueQuestionnaireChatTurnResponse {
  turnId: string;
  status: QuestionnaireChatTurnStatus;
  /**
   * The turn's input as its row stores it: the builder draws the turn's
   * bubble from it from the start (an objective edit's recorded change).
   */
  input: QuestionnaireTurnInput;
}

/**
 * What a turn was asked to do, as its row stores it (plan S4.1, S4.2): the
 * user's words or a typed command (exactly one), the card option the words
 * chose, and the clarification record the turn names. The builder sends a
 * turn from this shape, shows its bubble from it, and a Retry resends it as
 * it is, live or after a reload.
 */
export interface QuestionnaireTurnInput {
  /** The user's words; null for a command. */
  message: string | null;
  /** The typed UI fact the turn carries instead of words; null for words. */
  command: QuestionnaireTurnCommand | null;
  /** The card option the words chose (plan S3.3, L16); null for none. */
  optionId: string | null;
  /**
   * The record the turn names (the enqueue's `answers`); null when it named
   * none. The record stays `open` until the turn commits. Whether the turn
   * answers it or supersedes it is `turnAnswersClarification`.
   */
  answersClarificationId: string | null;
}

export interface QuestionnaireChatTurnSnapshot {
  turnId: string;
  draftId: string;
  status: QuestionnaireChatTurnStatus;
  stage: QuestionnaireChatTurnStage | null;
  total: number | null;
  /**
   * Why the turn stopped. On a `failed` turn nothing was saved. On a
   * `completed` turn it is set only when the turn stopped after a commit
   * landed (a pasted collection saved, then its Build failed): what was sent
   * is saved and the rest did not run (#3). Its Retry is the `resume`
   * command.
   */
  errorCode: string | null;
  errorMessage: string | null;
  failureCodes: Array<string> | null;
  failureDetails: Array<QuestionnaireChatTurnFailureDetail> | null;
  /**
   * The failing owner's persisted retry verdict, on a `failed` turn and on a
   * `completed` one with a cause alike; null when it gave none.
   */
  retryable: boolean | null;
  draftRevisionAfter: number | null;
  /**
   * The draft revision the turn was enqueued against; null on rows written
   * before it was kept. A failed turn commits nothing, so while the draft is
   * still at this revision the failure is the latest thing that happened to
   * the draft, and its Retry is offered (plan S4.4).
   */
  draftRevisionBefore: number | null;
  /**
   * What the turn was asked, off its row (plan S4.1, S4.2). A reload shows a
   * running turn's bubble from it, and a failed turn's Retry resends it. The
   * readback is the owner's only.
   */
  input: QuestionnaireTurnInput;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/**
 * Active-turn readback. `chatState`/`revision` are populated once the turn is
 * terminal so a subscriber who missed the live status can settle.
 */
export interface QuestionnaireChatTurnReadback {
  turn: QuestionnaireChatTurnSnapshot | null;
  stale?: boolean;
  chatState?: QuestionnaireChatState;
  revision?: number | null;
}
